import React from 'react';
import styled, { css } from 'styled-components';
import Button from '../ui_components/button/button';
import {
  Modal,
  ModalActions as ModalActionsE,
  ModalContent,
  ModalHeader as ModalHeaderE,
  ModalProps,
  ModalCloseIcon,
} from '../ui_components/modal';
import { breakpoints, theme } from '../theme';
import WarningIcon from '../assets/icons/red_warning.svg';

const noop = () => {
  /* ignore */
};

interface BundleSetupModalProps
  extends Pick<
    ModalProps,
    | 'uiActive'
    | 'uiOnRequestClose'
    | 'uiCloseOnEsc'
    | 'uiCloseOnOutsideClick'
    | 'uiTransitionTimeout'
    | 'uiOnClose'
    | 'className'
  > {
  onRetry(): void;
  onExportWithoutBundling(): void;
  missingItems?: string[];
}

const StyledModal = styled(Modal)`
  max-width: 560px;
  border-radius: 0.5rem;
  border: solid ${theme.colors.grayRGBA} 1px;
  ${breakpoints.down(
    'sm',
    css`
      max-width: 90%;
    `
  )};
`;

const Header = styled(ModalHeaderE)`
  color: black;
  border-bottom: none;
  padding-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseIcon = styled(ModalCloseIcon)`
  margin-left: auto;
  margin-bottom: auto;
  width: 17.23px !important;
  height: 17.23px !important;
`;

const Content = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${theme.colors.grayRGBA};
`;

const MissingList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #222;
`;

const Actions = styled(ModalActionsE)`
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-top: none;
  flex-wrap: wrap;
  gap: 12px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
`;

const BasicButton = styled(Button)`
  box-shadow: none;
  border: 1px solid #c6c6c6;
  border-radius: 0.5em;
  display: block;
`;

const PrimaryButton = styled(BasicButton)`
  border-radius: 5px;
  box-shadow: none;
  line-height: 0.6rem;
  &:hover {
    background: #c6c6c6;
  }
`;

const SecondaryButton = styled(Button)`
    border-radius: 5px !important;
    box-shadow: none !important;
    line-height: 0.6rem !important;
    background-color: #f6f6f6 !important;
    color: #222222 !important;
    border: none !important;
    border: 1px solid #cdcdcd !important;
    margin-right: auto !important !important;
    &:hover {
        background: #eeeeee !important;
    }
`;

const BundleSetupModal: React.FC<BundleSetupModalProps> = ({
  uiActive,
  uiOnRequestClose = noop,
  uiCloseOnEsc,
  uiCloseOnOutsideClick,
  uiTransitionTimeout,
  uiOnClose,
  className,
  onRetry,
  onExportWithoutBundling,
  missingItems,
}) => {
  return (
    <StyledModal
      className={className}
      uiActive={uiActive}
      uiOnRequestClose={uiOnRequestClose}
      uiDefaultSizes={false}
      uiOnClose={uiOnClose}
      uiTransitionTimeout={uiTransitionTimeout}
      uiCloseOnOutsideClick={uiCloseOnOutsideClick}
      uiCloseOnEsc={uiCloseOnEsc}
    >
      <Header>
        <WarningIcon style={{width:"30px", height:'30px'}}/>
        <span>Your QuickBooks account is not set up for Bundling</span>
        <CloseIcon onClick={uiOnRequestClose} />
      </Header>
      <Content>
        <p>
          We are unable to export your bundled items. Complete <a rel="noopener noreferrer" target="_blank" href="https://quipa.crisp.help/en/article/qbo-bundling-setup-h3lrqd/?bust=1764782701259">QBO Bundle Setup</a> and retry or export
          without bundling.
        </p>
        {missingItems && missingItems.length > 0 && (
          <div>
            <strong>Missing bundle items in QuickBooks:</strong>
            <MissingList>
              {missingItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </MissingList>
          </div>
        )}
      </Content>
      <Actions>
        <Buttons>
          <SecondaryButton type="button" uiColor="secondary" onClick={onExportWithoutBundling}>
            Export without Bundling
          </SecondaryButton>
          <PrimaryButton type="button" uiColor="primary" onClick={onRetry}>
            Retry
          </PrimaryButton>
        </Buttons>
      </Actions>
    </StyledModal>
  );
};

export default BundleSetupModal;
